import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Copy, FileImage } from "lucide-react";
import { ReceiptViewer } from "@/components/wallet/ReceiptViewer";
import { api, errMsg, fmtAZN } from "@/lib/api";
import { STATUS } from "@/components/wallet/TxList";

const FILTERS = [["pending", "Gözləyən"], ["deposit", "Depozitlər"], ["withdraw", "Çıxarışlar"], ["", "Hamısı"]];

const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Kopyalandı"); };

export const TxAdmin = ({ onChange }) => {
  const [txs, setTxs] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/transactions", { params: filter === "pending" ? { status: "pending" } : {} });
    setTxs(filter === "deposit" || filter === "withdraw" ? data.filter((t) => t.type === filter) : data);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/transactions/${id}/${action}`);
      toast.success(action === "approve" ? "Təsdiqləndi" : "Ləğv edildi");
      load(); onChange?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map(([v, l]) => (
          <button key={l} onClick={() => setFilter(v)} className={`rounded-full px-5 py-2 text-sm ${filter === v ? "btn-gold" : "btn-ghost"}`} data-testid={`admin-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#111] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-tx-table">
          <thead className="text-xs uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
            <tr>{["İstifadəçi", "Növ", "Ödəniş hesabı", "Ad", "Məbləğ", "Tarix", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {txs.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-zinc-500">Sorğu yoxdur</td></tr>}
            {txs.map((t) => (
              <tr key={t.id} className="border-b border-white/5" data-testid={`admin-tx-${t.id}`}>
                <td className="px-4 py-3">{t.user_email}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={t.type === "deposit" ? "text-emerald-400" : "text-[#FFD700]"}>{t.type === "deposit" ? "Depozit" : "Çıxarış"}</span>
                  <span className="text-zinc-500"> · {t.method === "m10" ? "m10" : "Kart"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-mono text-sm">
                    <span data-testid={`admin-tx-account-${t.id}`}>{t.account}</span>
                    {t.type === "withdraw" && <button onClick={() => copy(t.account)} className="p-1.5 rounded-md hover:bg-white/10" data-testid={`copy-account-${t.id}`}><Copy size={14} /></button>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div>{t.sender_name || "-"}</div>
                  {t.receipt_path && <button onClick={() => setReceipt(t)} className="mt-1 text-xs text-[#FFD700] flex items-center gap-1 hover:underline" data-testid={`view-receipt-${t.id}`}><FileImage size={13} /> Qəbzə bax</button>}
                </td>
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{fmtAZN(t.amount)}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{new Date(t.created_at).toLocaleString("az-AZ")}</td>
                <td className="px-4 py-3"><span className={`text-xs rounded-full px-3 py-1 whitespace-nowrap ${STATUS[t.status][1]}`}>{STATUS[t.status][0]}</span></td>
                <td className="px-4 py-3">
                  {t.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => act(t.id, "approve")} className="rounded-full px-3 py-1.5 text-xs flex items-center gap-1 bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/30 transition-colors" data-testid={`approve-${t.id}`}><Check size={14} /> {t.type === "withdraw" ? "Ödənildi" : "Təsdiq"}</button>
                      <button onClick={() => act(t.id, "reject")} className="rounded-full px-3 py-1.5 text-xs flex items-center gap-1 bg-red-500/15 text-red-400 hover:bg-red-500/30 transition-colors" data-testid={`reject-${t.id}`}><X size={14} /> Ləğv</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReceiptViewer tx={receipt} onClose={() => setReceipt(null)} onAct={(a) => { act(receipt.id, a); setReceipt(null); }} />
    </div>
  );
};
